import { Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Financials } from "../../interfaces/Customer";
import { isFloat, isInteger } from "../../services/utils";

const FinancialsForm: React.FC<{
    financials: Financials;
    setFinancials: (financials: Financials) => void;
    setFinancialsValid: (valid: boolean) => void;
    modelChanged: boolean;
}> = ({ financials, setFinancials, setFinancialsValid, modelChanged }) => {
    // Local state to allow temporary string values
    const [fields, setFields] = useState<Record<string, string>>({
        netRevenue: financials.netRevenue.toString(),
        ebit: financials.ebit.toString(),
        netProfit: financials.netProfit.toString(),
        tax: financials.tax.toString(),
        positionsCount: financials.positionsCount.toString(),
        employeesCount: financials.employeesCount.toString(),
        managementPositions: financials.managementPositions.toString(),
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const setAndValidateFields = (newFields: Record<string, string>) => {
        const newErrors: { [key: string]: string } = {};
        const newFinancials = { ...financials };


        if (!isFloat(newFields.netRevenue)) {
            newErrors.netRevenue = "Przychód netto musi być liczbą.";
        } else {
            newFinancials.netRevenue = parseFloat(newFields.netRevenue.replace(",", "."));
        }

        if (!isFloat(newFields.ebit)) {
            newErrors.ebit = "EBIT musi być liczbą.";
        } else {
            newFinancials.ebit = parseFloat(newFields.ebit.replace(",", "."));
        }

        if (!isFloat(newFields.netProfit)) {
            newErrors.netProfit = "Zysk netto musi być liczbą.";
        } else {
            newFinancials.netProfit = parseFloat(newFields.netProfit.replace(",", "."));
        }

        if (isFloat(newFields.ebit) && isFloat(newFields.netProfit)) {
            const calculatedTax = parseFloat(newFields.ebit.replace(",", ".")) - parseFloat(newFields.netProfit.replace(",", "."));
            newFinancials.tax = calculatedTax;
            newFields.tax = calculatedTax.toFixed(2).toString().replace(".", ",");
        }

        if (!isFloat(newFields.positionsCount) || parseFloat(newFields.positionsCount.replace(",", ".")) < 0) {
            newErrors.positionsCount = "Liczba etatów musi być liczbą dodatnią.";
        } else {
            newFinancials.positionsCount = parseFloat(newFields.positionsCount.replace(",", "."));
        }

        if (!isInteger(newFields.employeesCount) || parseInt(newFields.employeesCount) < 0) {
            newErrors.employeesCount = "Liczba pracowników musi być dodatnią liczbą całkowitą.";
        } else {
            newFinancials.employeesCount = parseInt(newFields.employeesCount, 10);
        }

        if (!isInteger(newFields.managementPositions) || parseInt(newFields.managementPositions) < 0) {
            newErrors.managementPositions = "Stanowiska kierownicze musi być dodatnią liczbą całkowitą.";
        } else {
            newFinancials.managementPositions = parseInt(newFields.managementPositions, 10);
        }

        setErrors(newErrors);
        setFields(newFields);
        setFinancials(newFinancials);
        setFinancialsValid(Object.keys(newErrors).length === 0);
    }

    // Update fields when model changes from outside
    useEffect(() => {
        setAndValidateFields({
            netRevenue: financials.netRevenue.toString().replace(".", ","),
            ebit: financials.ebit.toString().replace(".", ","),
            netProfit: financials.netProfit.toString().replace(".", ","),
            tax: financials.tax.toFixed(2).toString().replace(".", ","),
            positionsCount: financials.positionsCount.toString().replace(".", ","),
            employeesCount: financials.employeesCount.toString(),
            managementPositions: financials.managementPositions.toString(),
        });
    }, [modelChanged]);

    // Helper to handle changes
    const handleChange = (field: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAndValidateFields({ ...fields, [field]: value });
    };

    return (
        <Box component="form" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, padding: 0 }}>
            <TextField
                label="Przychód netto"
                value={fields.netRevenue}
                error={!!errors.netRevenue}
                helperText={errors.netRevenue || ""}
                onChange={handleChange("netRevenue")}
            />
            <TextField
                label="EBIT"
                value={fields.ebit}
                error={!!errors.ebit}
                helperText={errors.ebit || ""}
                onChange={handleChange("ebit")}
            />
            <TextField
                label="Zysk netto"
                value={fields.netProfit}
                error={!!errors.netProfit}
                helperText={errors.netProfit || ""}
                onChange={handleChange("netProfit")}
            />
            <TextField
                label="Podatek"
                value={fields.tax}
                error={!!errors.tax}
                helperText={errors.tax || ""}
            />
            <TextField
                label="Liczba etatów"
                value={fields.positionsCount}
                error={!!errors.positionsCount}
                helperText={errors.positionsCount || ""}
                onChange={handleChange("positionsCount")}
            />
            <TextField
                label="Liczba pracowników"
                value={fields.employeesCount}
                error={!!errors.employeesCount}
                helperText={errors.employeesCount || ""}
                onChange={handleChange("employeesCount")}
            />
            <TextField
                label="Stanowiska kierownicze"
                value={fields.managementPositions}
                error={!!errors.managementPositions}
                helperText={errors.managementPositions || ""}
                onChange={handleChange("managementPositions")}
            />
        </Box>
    );
}

export default FinancialsForm;
// ...existing code...